// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class PermissionsService {
  moduleTypes: { label: string; value: string }[] =
    this.configService.availableModules;

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private configService: ConfigService
  ) {}

  /**
   * Create a new permission
   *
   * @since 1.0.0
   *
   * @param permission Permission data
   * @returns Observable<any>
   */
  create(permission: any): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      "permissions",
      permission,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get permissions list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      "permissions",
      params,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get an exportation file of all permissions
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        "permissions/export",
        {},
        { responseType: "blob" },
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get given permissionId data from back-end
   *
   * @since 1.0.0
   *
   * @param permissionId ID of the permission to get
   */
  get(permissionId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `permissions/${permissionId}`,
        undefined,
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Update permission with given permissionId with given permission data
   *
   * @since 1.0.0
   *
   * @param permissionId ID of permission to update
   * @param data New permission's data
   */
  update(permissionId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PUT",
      `permissions/${permissionId}`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Delete permanently a permission
   *
   * @since 1.0.0
   *
   * @param permissionId ID of the permission
   */
  delete(permissionId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `permissions/${permissionId}`,
      undefined,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }
}
